import { Box, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { DailyLabellerStatsEntry } from "src/store/api/generatedApi";

export const DAILY_ACTIVITY_METRICS = [
  "totalTime",
  "totalTimeSpent",
  "totalSubmissions",
  "hoursPerHour",
] as const;

export type DailyActivityMetric = (typeof DAILY_ACTIVITY_METRICS)[number];

const extractValue = (
  entry: DailyLabellerStatsEntry,
  metric: DailyActivityMetric
): number | null => {
  switch (metric) {
    case "totalTime":
      return entry.time_labelled_seconds / 3600;
    case "totalTimeSpent":
      return entry.time_spent_labelling_seconds / 3600;
    case "totalSubmissions":
      return entry.submissions;
    case "hoursPerHour":
      return entry.time_spent_labelling_seconds > 0
        ? entry.time_labelled_seconds / entry.time_spent_labelling_seconds
        : null;
  }
};

export const LABELLING_PALETTE_KEY = "blue" as const;
export const REVIEWING_PALETTE_KEY = "orange" as const;

const Y_AXIS_UNIT: Record<DailyActivityMetric, string> = {
  totalTime: "(h)",
  totalTimeSpent: "(h)",
  totalSubmissions: "",
  hoursPerHour: "(h/h)",
};

const STACKABLE_METRICS: ReadonlySet<DailyActivityMetric> = new Set([
  "totalTime",
  "totalTimeSpent",
  "totalSubmissions",
]);

const sortByDate = (stats: readonly DailyLabellerStatsEntry[]) =>
  sorted([...stats], (s) => s.date);

type DailyActivityChartProps = Readonly<{
  labellingStats: readonly DailyLabellerStatsEntry[];
  reviewingStats: readonly DailyLabellerStatsEntry[];
  selectedMetric: DailyActivityMetric;
}>;

export const DailyActivityChart = ({
  labellingStats,
  reviewingStats,
  selectedMetric,
}: DailyActivityChartProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { data: palette } = theme.palette;

  const labelColor = palette[LABELLING_PALETTE_KEY];
  const reviewColor = palette[REVIEWING_PALETTE_KEY];

  const data: Partial<PlotData>[] = useMemo(() => {
    const traces: Partial<PlotData>[] = [];
    if (labellingStats.length > 0) {
      const sortedLabelling = sortByDate(labellingStats);
      traces.push({
        x: sortedLabelling.map((s) => s.date),
        y: sortedLabelling.map((s) => extractValue(s, selectedMetric)),
        type: "bar" as const,
        name: t("label"),
        marker: { color: labelColor },
      });
    }
    if (reviewingStats.length > 0) {
      const sortedReviewing = sortByDate(reviewingStats);
      traces.push({
        x: sortedReviewing.map((s) => s.date),
        y: sortedReviewing.map((s) => extractValue(s, selectedMetric)),
        type: "bar" as const,
        name: t("review"),
        marker: { color: reviewColor },
      });
    }
    return traces;
  }, [
    labellingStats,
    reviewingStats,
    selectedMetric,
    t,
    labelColor,
    reviewColor,
  ]);

  const barmode = STACKABLE_METRICS.has(selectedMetric) ? "stack" : "group";

  const yAxisTitle = [t(selectedMetric), Y_AXIS_UNIT[selectedMetric]]
    .filter(Boolean)
    .join(" ");

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Plot
        data={data}
        layout={{
          autosize: true,
          xaxis: { type: "date" },
          yaxis: { title: { text: yAxisTitle } },
          margin: { l: 60, r: 30, t: 30, b: 60 },
          hovermode: "x unified",
          barmode,
          showlegend: false,
          plot_bgcolor: "transparent",
          paper_bgcolor: "transparent",
        }}
        config={{
          responsive: true,
          displayModeBar: false,
          displaylogo: false,
        }}
        style={{ width: "100%", height: "400px" }}
        useResizeHandler
      />
    </Box>
  );
};
